import { apiError } from '../apiError';
import axiosInstance from '../axios.config';
import { API_ENDPOINTS } from '../endpoints';
import { ApiResponse } from 'types';

/**
 * Call hangup metrics — per-call disposition / hangup_cause / forensic CDR,
 * read from the tenant DB (the runtime writes it there). Backend:
 * teeoff-cpass-backend /calls/hangup-metrics.
 */
export interface HangupMetric {
  unique_id: string;
  linked_id?: string;
  caller_id?: string;
  dialed_number?: string;
  business_id?: string;
  business_name?: string;
  service_id?: string;
  channel?: string;
  disposition?: string;
  hangup_cause?: number;
  hangup_cause_txt?: string;
  channel_state?: string;
  last_app?: string;
  call_started_at?: string;
  call_ended_at?: string;
  duration_seconds?: number;
  created_at?: string;
  // detail-only:
  hangup_meta?: any;
  raw_hangup?: any;
  raw_cdr?: any;
}

export interface HangupSummaryRow {
  disposition?: string;
  hangup_cause?: number;
  hangup_cause_txt?: string;
  count: number;
}

export interface ListParams {
  organizationId?: string;
  page?: number;
  perPage?: number;
  disposition?: string;
  hangupCause?: number;
  dateFrom?: string;
  dateTo?: string;
  callerId?: string;
  dialedNumber?: string;
}

export interface Paged<T> { data: T[]; pagination: { page: number; perPage: number; total: number; totalPages: number }; }

// Shared so a 403 reads as a refusal rather than a load failure.
const err = (e: any, msg: string) => apiError(e, msg);

export const callHangupMetricsService = {
  list: async (params: ListParams): Promise<ApiResponse<Paged<HangupMetric>>> => {
    // Convert frontend 1-based page to backend 0-based page (only when a page is provided)
    const apiParams = params.page !== undefined ? { ...params, page: Math.max(0, params.page - 1) } : params;
    try { return (await axiosInstance.get(API_ENDPOINTS.CALL_HANGUP_METRICS, { params: apiParams })).data; }
    catch (e: any) { return err(e, 'Failed to load hangup metrics'); }
  },
  summary: async (organizationId?: string, dateFrom?: string, dateTo?: string): Promise<ApiResponse<HangupSummaryRow[]>> => {
    try { return (await axiosInstance.get(API_ENDPOINTS.CALL_HANGUP_METRICS_SUMMARY, { params: { organizationId, dateFrom, dateTo } })).data; }
    catch (e: any) { return err(e, 'Failed to load summary'); }
  },
  detail: async (uniqueId: string, organizationId?: string): Promise<ApiResponse<HangupMetric>> => {
    try { return (await axiosInstance.get(API_ENDPOINTS.CALL_HANGUP_METRIC_DETAIL(uniqueId), { params: { organizationId } })).data; }
    catch (e: any) { return err(e, 'Failed to load detail'); }
  },
};
